import { AdminPropertyContracts, plansAll } from "types/gql-types";

type Row = AdminPropertyContracts["propertyContracts"][number];
type PlanRow = plansAll["plansAll"][number];

export const toNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

export const formatCurrency = (value: number): string => {
  const hasDecimals = !Number.isInteger(value);
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const monthlyLabelFrom = (value: unknown): string => {
  const fallback = toNumberOrNull(value);
  if (fallback === null) return "-";
  return `${formatCurrency(fallback)}/month`;
};

export const discountedUnitPrice = (
  monthlyPerUnit: number,
  annualDiscountPercent: number,
  billingFrequency: string,
): number => {
  if (billingFrequency !== "annually" || annualDiscountPercent <= 0) {
    return monthlyPerUnit;
  }
  return roundToCents(monthlyPerUnit * (1 - (annualDiscountPercent / 100)));
};

/** Computes the table's "Price / Unit" label from the row + its plan's default pricing. */
export const priceDisplayFor = (row: Row, plansById: Map<string, PlanRow>): string => {
  const planId = row.planId || "";
  const plan = plansById.get(planId);
  const pricing = plan?.defaultPricing;

  if (!pricing) return monthlyLabelFrom(row.pricePerUnit);

  const baseMonthly = toNumberOrNull(pricing.minimumPrice);
  const threshold = toNumberOrNull(pricing.unitThreshold);
  const monthlyPerUnit = toNumberOrNull(pricing.monthlyPricePerUnit);
  const annualDiscountPercent = toNumberOrNull(pricing.annualDiscountPercent) || 0;
  const units = toNumberOrNull(row.unitCount) || 0;
  const billingFrequency = (row.billingFrequency || "").toLowerCase();
  const hasAnnualDiscount = billingFrequency === "annually" && annualDiscountPercent > 0;

  const thresholdExceeded = threshold !== null && monthlyPerUnit !== null && units > threshold;

  if (thresholdExceeded && monthlyPerUnit !== null) {
    const effectiveUnitPrice = discountedUnitPrice(
      monthlyPerUnit,
      annualDiscountPercent,
      billingFrequency,
    );
    const discountLabel = hasAnnualDiscount ? " (annual discount)" : "";
    return `${formatCurrency(effectiveUnitPrice)}/unit/month${discountLabel}`;
  }

  if (baseMonthly !== null) return `${formatCurrency(baseMonthly)}/month`;

  return monthlyLabelFrom(row.pricePerUnit);
};
